import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AppButton, AppCard, AppInput } from "@/components/ui";
import { CheckCircle2, Plus, ShieldCheck, Zap } from "lucide-react";
import { gql } from "@apollo/client/core";
import { apolloClient } from "@/graphql/client";

const JOIN_WAITLIST_MUTATION = gql`
  mutation JoinWaitlist($input: JoinWaitlistInput!) {
    joinWaitlist(input: $input) {
      success
      message
    }
  }
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface JoinWaitlistData {
  joinWaitlist: {
    success: boolean;
    message: string | null;
  };
}

type FormStatus = "idle" | "loading" | "success" | "error";

/**
 * Waitlist email form with idle/loading/success/error states.
 *
 * Client-side validation first, then server call.
 * Success is persistent — no re-submit after success.
 * Duplicate email returns success (idempotent).
 * Rosé Pine Dawn luxury aesthetic with large rounded form.
 */
export function WaitlistForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validate = (value: string): string | null => {
    if (!value.trim()) {
      return t("landing.cta.errors.required");
    }
    if (!EMAIL_REGEX.test(value.trim())) {
      return t("landing.cta.errors.invalid");
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const validationError = validate(email);
    if (validationError) {
      setErrorMessage(validationError);
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMessage(null);

    try {
      const result = await apolloClient.mutate<JoinWaitlistData>({
        mutation: JOIN_WAITLIST_MUTATION,
        variables: { input: { email: email.trim() } },
      });

      if (result.data?.joinWaitlist?.success) {
        setStatus("success");
      } else {
        setErrorMessage(
          result.data?.joinWaitlist?.message ??
            t("landing.cta.errors.generic"),
        );
        setStatus("error");
      }
    } catch (error: unknown) {
      const gqlError =
        error &&
        typeof error === "object" &&
        "graphQLErrors" in error &&
        Array.isArray((error as { graphQLErrors: unknown[] }).graphQLErrors)
          ? (error as { graphQLErrors: { extensions?: { code?: string }; message?: string }[] })
              .graphQLErrors[0]
          : null;

      if (gqlError?.extensions?.code === "RATE_LIMITED") {
        setErrorMessage(t("landing.cta.errors.rateLimited"));
      } else {
        setErrorMessage(t("landing.cta.errors.generic"));
      }
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <AppCard
        className="bg-success/5 p-12 rounded-[3rem] border border-success/20 max-w-2xl mx-auto"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="w-16 h-16 text-success mx-auto mb-6" />
        <h3 className="text-3xl font-bold text-foreground">
          {t("landing.cta.success")}
        </h3>
        <p className="text-foreground-secondary mt-3 text-lg">
          {t("landing.cta.successDetail")}
        </p>
      </AppCard>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto space-y-8"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-4 p-3 bg-surface border border-border rounded-[2.5rem] focus-within:ring-4 focus-within:ring-success/10 shadow-xl transition-all">
        <AppInput
          type="email"
          required
          disabled={status === "loading"}
          placeholder={t("landing.cta.placeholder")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") {
              setStatus("idle");
              setErrorMessage(null);
            }
          }}
          className="flex-1 bg-transparent px-10 py-6 focus:outline-none focus:bg-transparent focus:border-transparent focus:ring-0 focus-visible:bg-transparent focus-visible:border-transparent focus-visible:ring-0 text-foreground text-lg placeholder-foreground-muted disabled:opacity-50 border-0 shadow-none rounded-none"
          style={{ backgroundColor: "transparent", borderColor: "transparent" }}
          aria-invalid={status === "error"}
          aria-describedby={errorMessage ? "waitlist-error" : undefined}
        />
        <AppButton
          type="submit"
          isDisabled={status === "loading"}
          className="px-14 py-6 h-auto min-w-0 bg-success text-background font-bold rounded-[2rem] hover:bg-foreground hover:scale-105 transition-all gap-3 shadow-xl"
        >
          {status === "loading"
            ? t("landing.cta.submitting")
            : t("landing.cta.submit")}{" "}
          <Plus className="w-5 h-5" />
        </AppButton>
      </div>

      {/* Error message */}
      {status === "error" && errorMessage && (
        <p id="waitlist-error" className="text-danger text-sm text-center" role="alert">
          {errorMessage}
        </p>
      )}

      {/* Trust badges */}
      <div className="flex justify-center gap-12 pt-8">
        <div className="flex flex-col items-center">
          <ShieldCheck className="w-5 h-5 text-success mb-2" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-foreground-muted">
            Security Sync
          </span>
        </div>
        <div className="flex flex-col items-center">
          <Zap className="w-5 h-5 text-success mb-2" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-foreground-muted">
            12 Beta Slots Left
          </span>
        </div>
      </div>
    </form>
  );
}
